import {
  logout,
  useUser,
  useUserKey,
  REMOTE_SIGN_IN_ERROR,
  remoteSignInFailed,
  REMOTE_SIGN_IN_TIMEOUT,
  setRemoteSignInAvaliable,
  useUserRealm
} from '../../features/user/ssoSlice';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { KEYCLOAK_CONFIG } from '../../config';
import { RouteKey } from '../../config/page-key';
import moment from 'moment';
import { StorageKey } from '../../features/localStorage/storageKey';
import { useGetUserInfoQuery } from '../../features/user/userApi';
import Keycloak from 'keycloak-js';

export function useSSO() {
  const user = useUser();
  const { error } = useGetUserInfoQuery({userId : user.id}, {skip:user.id=== undefined});
  const userKey = useUserKey();
  const dispatch = useDispatch();
  const systemKcConfig = useKCConfig();
  const history = useHistory();

  useEffect(() => {
    if (user == null || userKey == null || user.auth?.keycloakConfig == null) return;
    if (user != null) {
      //if the auth server config is changed or the API somehow return 401, force logoff instead of stuck in landing page
      if (user.auth?.keycloakConfig?.["url"] != systemKcConfig["url"] || error?.originalStatus === 401) {
        history.push(RouteKey.LOGOUT, {forceLogoff: true});
        return;
      }
    }

    const keycloakConfig = user.auth.keycloakConfig;
    if (window.keycloak == null)
      window.keycloak = new Keycloak({
        ...keycloakConfig,
        url: keycloakConfig.url ?? keycloakConfig['auth-server-url']
      });

    const logoutUser = () => {
      let redirectUrl = '/sso';

      if (keycloakConfig.realm) {
        redirectUrl += '?realm=' + keycloakConfig.realm;
      }

      dispatch(logout());
      dispatch(remoteSignInFailed());
      window.location.href = redirectUrl;
    };

    window.keycloak.onAuthLogout = logoutUser;

    window.keycloak.onTokenExpired = () => {
      window.keycloak
        .updateToken(30)
        .catch(e => {
          logoutUser();
          dispatch(remoteSignInFailed(REMOTE_SIGN_IN_ERROR.KC_AUTH_ERROR));
        });
    };

    window.localStorage.setItem(StorageKey.USER_ACTION_TIME, moment().format('X'));
    
    const runSSO = async () => {
      const { error: ssoError } = await checkSSO(true);

      dispatch(
        setRemoteSignInAvaliable({
          remoteSignInAvaliable: !ssoError,
          remoteSignInError: ssoError
        })
      );
    };

    runSSO();
  }, [user, userKey, dispatch, error]);
}

const checkSSO = async (authRequired = false) => {
  return new Promise(resolve => {
    if (window.keycloak.didInitialize) {
      resolve({
        error:
          !window.keycloak?.authenticated && authRequired
            ? REMOTE_SIGN_IN_ERROR.KC_AUTH_ERROR
            : null
      });
    } else {
      window.keycloak
        .init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin,
          flow: 'standard',
          messageReceiveTimeout: REMOTE_SIGN_IN_TIMEOUT,
          checkLoginIframe: false
        })
        .then(authenticated =>
          resolve({
            error: !authenticated && authRequired ? REMOTE_SIGN_IN_ERROR.KC_AUTH_ERROR : null
          })
        )
        .catch(e => {
          resolve({ error: REMOTE_SIGN_IN_ERROR.KC_INIT_ERROR });
        });
    }
  });
};

export const useKCConfig = () => {
  const history = useHistory();
  const userRealm = useUserRealm();
  return useMemo(() => {
    const search = history.location.search;
    const keycloakConfig = JSON.parse(KEYCLOAK_CONFIG);

    if (userRealm) {
      keycloakConfig.realm = userRealm;
    }

    if (search.trim() !== '' && search.toLowerCase().indexOf('realm') >= 0) {
      const keySets = search.substring(1).split('&');
      const realm = keySets.find(k => k.toLowerCase().startsWith('realm'))?.split('=')[1];
      keycloakConfig.realm = realm;
    }
    return keycloakConfig;
  }, [history, userRealm]);
};